import { Navigate, Outlet } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import type { UserRole } from '@/stores/appStore';
import { toast } from 'sonner';
import { useEffect } from 'react';

interface RoleRouteProps {
  allowedRoles: UserRole[];
}

export default function RoleRoute({ allowedRoles }: RoleRouteProps) {
  const { userRole, isLoading, orgFetched } = useAppStore();

  useEffect(() => {
    if (userRole && !allowedRoles.includes(userRole)) {
      toast.error('You do not have permission to access this page');
    }
  }, [userRole, allowedRoles]);

  // Show the spinner while:
  //   (a) the auth check is still running (isLoading), OR
  //   (b) auth is done but the org fetch hasn't completed yet (!orgFetched)
  //
  // Previously only (a) was checked. userRole is populated by the org fetch
  // in MainLayout which runs *after* initAuth() completes. In the gap between
  // auth done and org fetch done, isLoading was false but userRole was still
  // null — so RoleRoute immediately redirected every valid user to /dashboard
  // on every page refresh / hard load.
  if (isLoading || !orgFetched) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!userRole || !allowedRoles.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
